import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box, Dialog, DialogContent, Stack, Typography, CircularProgress,
} from '@mui/material';
import CheckRoundedIcon        from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon        from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon    from '@mui/icons-material/ArrowBackRounded';
import axios from 'axios';
import { AuthContext } from '../../components/signin/AuthContext';
import { useGrowSetup } from '../../GrowSetupContext';

// ── Shared styles ────────────────────────────────────────────────────────────

const ANIM_CSS = `
@keyframes wzSlideIn  { from { opacity:0; transform:translateX(40px) }  to { opacity:1; transform:translateX(0) } }
@keyframes wzSlideOut { from { opacity:0; transform:translateX(-40px) } to { opacity:1; transform:translateX(0) } }
@keyframes wzFadeUp   { from { opacity:0; transform:translateY(20px) }  to { opacity:1; transform:translateY(0) } }
@keyframes wzPop      { 0%{transform:scale(0.5);opacity:0} 60%{transform:scale(1.12)} 100%{transform:scale(1);opacity:1} }
@keyframes wzPulseRing{ 0%{box-shadow:0 0 0 0 #00d4ff55} 70%{box-shadow:0 0 0 14px #00d4ff00} 100%{box-shadow:0 0 0 0 #00d4ff00} }
@keyframes wzSpin     { to { transform:rotate(360deg) } }
@keyframes wzBlink    { 0%,100%{opacity:1} 50%{opacity:0} }
`;

// ── Device definitions ───────────────────────────────────────────────────────

const DEVICE_TYPES = [
  {
    key: 'klima',
    label: 'Klima-Kontroller',
    subtitle: 'Temperatur · Luftfeuchte · CO₂',
    icon: '🌡️',
    color: '#00d4ff',
    desc: 'Steuert Belüftung, Heizung und Klimasensoren in deinem Grow.',
  },
  {
    key: 'hydro',
    label: 'Hydro-Kontroller',
    subtitle: 'pH · EC · Bewässerung',
    icon: '💧',
    color: '#4488ff',
    desc: 'Überwacht und regelt dein Hydro- oder Bewässerungssystem.',
  },
];

// ── Step progress dots ───────────────────────────────────────────────────────

function StepDots({ step, total }) {
  return (
    <Stack direction="row" spacing={0.75} justifyContent="center" sx={{ mb: 3.5 }}>
      {Array.from({ length: total }).map((_, i) => (
        <Box key={i} sx={{
          width:  i === step ? 22 : 7,
          height: 7,
          borderRadius: '99px',
          background: i === step ? '#00d4ff' : i < step ? 'rgba(0,212,255,0.35)' : 'rgba(255,255,255,0.1)',
          transition: 'width 0.3s cubic-bezier(0.34,1.56,0.64,1), background 0.3s',
          boxShadow: i === step ? '0 0 10px #00d4ff88' : 'none',
        }} />
      ))}
    </Stack>
  );
}

// ── Icon button ──────────────────────────────────────────────────────────────

function IconBtn({ children, onClick, color = 'rgba(180,200,230,0.35)', sx = {} }) {
  return (
    <Box onClick={onClick} sx={{
      width: 36, height: 36, borderRadius: '12px', display: 'flex',
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      border: '1px solid rgba(255,255,255,0.08)',
      background: 'rgba(255,255,255,0.04)',
      color, transition: 'all 0.16s',
      '&:hover': { background: 'rgba(255,255,255,0.09)', color: '#e0ecff' },
      ...sx,
    }}>
      {children}
    </Box>
  );
}

// ── Step 0: Choose device type ───────────────────────────────────────────────

function StepChoose({ onSelect, dir }) {
  return (
    <Box sx={{ animation: `${dir > 0 ? 'wzSlideIn' : 'wzSlideOut'} 0.32s cubic-bezier(0.22,1,0.36,1) both` }}>
      <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#e0ecff', mb: 0.75, textAlign: 'center' }}>
        Welches Gerät möchtest du hinzufügen?
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: 'rgba(180,200,230,0.4)', textAlign: 'center', mb: 3 }}>
        Wähle den Gerätetyp — wir führen dich dann durch die Verbindung.
      </Typography>

      <Stack spacing={1.5}>
        {DEVICE_TYPES.map((d, i) => (
          <Box
            key={d.key}
            onClick={() => onSelect(d)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              p: 2.25, borderRadius: '16px', cursor: 'pointer',
              background: `${d.color}0a`,
              border: `1px solid ${d.color}22`,
              transition: 'all 0.2s',
              animation: `wzFadeUp 0.4s ${i * 80}ms both`,
              '&:hover': {
                background: `${d.color}18`,
                border: `1px solid ${d.color}55`,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 30px ${d.color}20`,
              },
            }}
          >
            <Box sx={{
              width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
              background: `${d.color}15`, border: `1px solid ${d.color}40`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24,
            }}>
              {d.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#e0ecff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                {d.label}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: `${d.color}99`, mt: 0.3 }}>
                {d.subtitle}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(180,200,230,0.4)', mt: 0.4, lineHeight: 1.4 }}>
                {d.desc}
              </Typography>
            </Box>
            <Box sx={{ color: `${d.color}66`, fontSize: '1.2rem', flexShrink: 0 }}>›</Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ── Segmented serial input (XXXX-XXXX-XXXX-XXXX) ────────────────────────────

function SerialInput({ value, onChange }) {
  // value: flat 16 digits string
  const inputsRef = useRef([]);
  const segments  = [0, 1, 2, 3];

  const getSegment = (seg) => value.slice(seg * 4, seg * 4 + 4);

  const handleSegmentChange = (seg, raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    const next = value.slice(0, seg * 4) + digits.padEnd(4, ' ').slice(0, 4) + value.slice((seg + 1) * 4);
    const cleaned = next.replace(/ /g, '');
    onChange(cleaned.padEnd(16, ' ').slice(0, 16));
    if (digits.length === 4 && seg < 3) {
      inputsRef.current[seg + 1]?.focus();
    }
  };

  const handleKeyDown = (seg, e) => {
    if (e.key === 'Backspace' && getSegment(seg) === '    ' && seg > 0) {
      inputsRef.current[seg - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && getSegment(seg).replace(/ /g,'').length === 4) {
      inputsRef.current[seg + 1]?.focus();
    }
    if (e.key === 'ArrowLeft') {
      inputsRef.current[seg - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 16).padEnd(16, ' ');
    onChange(pasted);
    inputsRef.current[3]?.focus();
  };

  return (
    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
      {segments.map((seg) => (
        <React.Fragment key={seg}>
          <Box
            component="input"
            ref={el => { inputsRef.current[seg] = el; }}
            value={getSegment(seg).trim()}
            onChange={e => handleSegmentChange(seg, e.target.value)}
            onKeyDown={e => handleKeyDown(seg, e)}
            onPaste={handlePaste}
            maxLength={4}
            inputMode="numeric"
            placeholder="0000"
            sx={{
              boxSizing: 'border-box',
              width: 76, height: 56, p: 0,
              textAlign: 'center', letterSpacing: '2px',
              fontFamily: 'monospace', fontSize: '1.15rem', fontWeight: 700,
              color: '#00d4ff',
              background: 'rgba(0,212,255,0.06)',
              border: '1px solid rgba(0,212,255,0.2)',
              borderRadius: '12px',
              outline: 'none',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:focus': {
                borderColor: '#00d4ff',
                boxShadow: '0 0 0 3px rgba(0,212,255,0.15)',
              },
              caretColor: '#00d4ff',
            }}
          />
          {seg < 3 && (
            <Typography sx={{ color: 'rgba(0,212,255,0.3)', fontWeight: 700, fontSize: '1.2rem', userSelect: 'none' }}>
              —
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Stack>
  );
}

// ── Step 1: Enter serial number ──────────────────────────────────────────────

function StepSerial({ device, serial, onSerialChange, onNext, dir }) {
  const isValid = serial.replace(/\s/g,'').length === 16;
  return (
    <Box sx={{ animation: `${dir > 0 ? 'wzSlideIn' : 'wzSlideOut'} 0.32s cubic-bezier(0.22,1,0.36,1) both` }}>
      {/* Device header */}
      <Stack direction="row" alignItems="center" spacing={1.5} justifyContent="center" sx={{ mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '12px',
          background: `${device.color}18`, border: `1px solid ${device.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>
          {device.icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, color: '#e0ecff', fontSize: '0.95rem' }}>
            {device.label}
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: `${device.color}88` }}>
            Seriennummer eingeben
          </Typography>
        </Box>
      </Stack>

      <Typography sx={{ textAlign: 'center', fontSize: '0.8rem', color: 'rgba(180,200,230,0.4)', mb: 2.5 }}>
        Die Seriennummer befindet sich auf der Rückseite deines Geräts.
      </Typography>

      {/* The big input */}
      <Box sx={{ mb: 3 }}>
        <SerialInput value={serial} onChange={onSerialChange} />
      </Box>

      {/* Hint card */}
      <Box sx={{
        p: 1.75, borderRadius: '12px',
        background: 'rgba(0,212,255,0.04)',
        border: '1px solid rgba(0,212,255,0.1)',
        mb: 3,
        animation: 'wzFadeUp 0.4s 120ms both',
      }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Typography sx={{ fontSize: 16, mt: 0.1 }}>📋</Typography>
          <Box>
            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,212,255,0.7)', fontWeight: 700, mb: 0.3 }}>
              Seriennummer nicht sichtbar?
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(180,200,230,0.4)', lineHeight: 1.5 }}>
              Drücke den Reset-Knopf am Gerät — die LED zeigt das Pairing-Signal. Prüfe danach die Aufkleber
              an der Unterseite des Gehäuses.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Confirm button */}
      <Box
        onClick={isValid ? onNext : undefined}
        sx={{
          py: 1.5, borderRadius: '14px', textAlign: 'center',
          cursor: isValid ? 'pointer' : 'not-allowed',
          background: isValid
            ? `linear-gradient(135deg, ${device.color}, ${device.color}88)`
            : 'rgba(255,255,255,0.05)',
          border: `1px solid ${isValid ? device.color + '66' : 'rgba(255,255,255,0.08)'}`,
          color: isValid ? '#0a0f1e' : 'rgba(180,200,230,0.2)',
          fontWeight: 800, fontSize: '0.88rem',
          transition: 'all 0.2s',
          boxShadow: isValid ? `0 4px 24px ${device.color}44` : 'none',
          '&:hover': isValid ? {
            boxShadow: `0 6px 32px ${device.color}66`,
            transform: 'translateY(-1px)',
          } : {},
        }}
      >
        Gerät verbinden
      </Box>
    </Box>
  );
}

// ── Step 2: Claiming / Result ────────────────────────────────────────────────

function StepResult({ device, status, error, onDone, dir }) {
  return (
    <Box
      sx={{ animation: `${dir > 0 ? 'wzSlideIn' : 'wzSlideOut'} 0.32s cubic-bezier(0.22,1,0.36,1) both`, textAlign: 'center' }}
    >
      {/* Status icon */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        {status === 'loading' && (
          <Box sx={{
            width: 80, height: 80, borderRadius: '50%',
            background: `${device.color}12`, border: `2px solid ${device.color}44`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'wzPulseRing 1.5s infinite',
          }}>
            <CircularProgress size={36} sx={{ color: device.color }} thickness={3} />
          </Box>
        )}
        {status === 'success' && (
          <Box sx={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'rgba(57,255,20,0.12)', border: '2px solid rgba(57,255,20,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'wzPop 0.55s cubic-bezier(0.22,1,0.36,1) both',
          }}>
            <CheckRoundedIcon sx={{ fontSize: 40, color: '#39ff14' }} />
          </Box>
        )}
        {status === 'error' && (
          <Box sx={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'rgba(255,59,48,0.12)', border: '2px solid rgba(255,59,48,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'wzPop 0.55s cubic-bezier(0.22,1,0.36,1) both',
          }}>
            <CloseRoundedIcon sx={{ fontSize: 40, color: '#ff3b30' }} />
          </Box>
        )}
      </Box>

      {status === 'loading' && (
        <>
          <Typography sx={{ fontWeight: 700, color: '#e0ecff', fontSize: '1rem', mb: 1 }}>
            Verbinde {device.label}…
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(180,200,230,0.35)' }}>
            Bitte warte einen Moment
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <Typography sx={{ fontWeight: 800, color: '#39ff14', fontSize: '1.05rem', mb: 1 }}>
            Erfolgreich verbunden!
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(180,200,230,0.4)', mb: 3 }}>
            Dein {device.label} ist jetzt mit diesem Grow verknüpft.
          </Typography>
          <Box onClick={onDone} sx={{
            py: 1.4, borderRadius: '14px', cursor: 'pointer',
            background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.35)',
            color: '#39ff14', fontWeight: 700, fontSize: '0.88rem',
            transition: 'all 0.18s',
            '&:hover': { background: 'rgba(57,255,20,0.2)' },
          }}>
            Fertig
          </Box>
        </>
      )}

      {status === 'error' && (
        <>
          <Typography sx={{ fontWeight: 800, color: '#ff3b30', fontSize: '1rem', mb: 1 }}>
            Verbindung fehlgeschlagen
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(180,200,230,0.35)', mb: 0.5 }}>
            {error || 'Unbekannter Fehler'}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(180,200,230,0.25)', mb: 3 }}>
            Prüfe die Seriennummer und stelle sicher, dass das Gerät eingeschaltet ist.
          </Typography>
          <Box onClick={onDone} sx={{
            py: 1.4, borderRadius: '14px', cursor: 'pointer',
            background: 'rgba(255,59,48,0.08)', border: '1px solid rgba(255,59,48,0.3)',
            color: '#ff3b30', fontWeight: 700, fontSize: '0.88rem',
            transition: 'all 0.18s',
            '&:hover': { background: 'rgba(255,59,48,0.15)' },
          }}>
            Erneut versuchen
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Main wizard dialog ───────────────────────────────────────────────────────

export default function DeviceWizard({ open, onClose, onSuccess }) {
  const { token }      = useContext(AuthContext);
  const { grow_setup } = useGrowSetup();

  const [step,   setStep]   = useState(0);
  const [dir,    setDir]    = useState(1);        // 1=forward, -1=backward
  const [device, setDevice] = useState(null);
  const [serial, setSerial] = useState(''.padEnd(16, ' '));
  const [status, setStatus] = useState(null);     // 'loading'|'success'|'error'
  const [error,  setError]  = useState('');

  const reset = () => {
    setStep(0); setDir(1); setDevice(null);
    setSerial(''.padEnd(16, ' ')); setStatus(null); setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const goTo = (next) => {
    setDir(next > step ? 1 : -1);
    setStep(next);
  };

  const handleSelectDevice = (d) => {
    setDevice(d); setDir(1); setStep(1);
  };

  const handleClaim = async () => {
    setDir(1); setStep(2); setStatus('loading');
    const code = serial.replace(/\s/g, '');
    // Format: XXXX-XXXX-XXXX-XXXX
    const formatted = [0,4,8,12].map(i => code.slice(i, i+4)).join('-');
    try {
      await axios.post(`/new_device/${grow_setup}`, {
        data: { serial_number: formatted },
      }, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      setStatus('success');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Verbindung fehlgeschlagen');
      setStatus('error');
    }
  };

  const handleDone = () => {
    if (status === 'success') { onSuccess?.(); handleClose(); }
    else { reset(); }
  };

  const STEPS = 3;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(4,7,20,0.97)',
          border: '1px solid rgba(0,212,255,0.18)',
          borderRadius: '24px',
          backdropFilter: 'blur(32px)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.7), 0 0 60px rgba(0,212,255,0.08)',
          overflow: 'hidden',
        },
      }}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', background: 'rgba(0,0,10,0.6)' } } }}
    >
      <style>{ANIM_CSS}</style>

      {/* Top bar */}
      <Box sx={{
        px: 2.5, pt: 2.5, pb: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {step > 0 && step < 2 && (
            <IconBtn onClick={() => goTo(step - 1)}>
              <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
            </IconBtn>
          )}
          <Typography sx={{
            fontSize: '0.62rem', fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.14em',
            color: 'rgba(0,212,255,0.5)',
          }}>
            {step === 0 ? 'Gerät hinzufügen' : step === 1 ? 'Seriennummer' : 'Verbinden'}
          </Typography>
        </Box>
        <IconBtn onClick={handleClose}>
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconBtn>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
        <StepDots step={step} total={STEPS} />

        {step === 0 && (
          <StepChoose onSelect={handleSelectDevice} dir={dir} />
        )}
        {step === 1 && device && (
          <StepSerial
            device={device}
            serial={serial}
            onSerialChange={setSerial}
            onNext={handleClaim}
            dir={dir}
          />
        )}
        {step === 2 && device && (
          <StepResult
            device={device}
            status={status}
            error={error}
            onDone={handleDone}
            dir={dir}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
